import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ChevronRight, Briefcase, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { ThemeToggle } from "@/components/ThemeProvider";
import { ExpressProgressBar } from "@/components/partner/ExpressProgressBar";
import { PropertyBasicsScreen, isPropertyBasicsComplete } from "@/components/partner/PropertyBasicsScreen";
import { LayoutFeaturesScreen, isLayoutFeaturesComplete } from "@/components/partner/LayoutFeaturesScreen";
import { AssessmentContextScreen, isAssessmentContextComplete } from "@/components/partner/AssessmentContextScreen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCREEN_TITLES = [
  { title: "Property Basics", subtitle: "Tell us about the property" },
  { title: "Layout & Features", subtitle: "What's the property like inside?" },
  { title: "Assessment Context", subtitle: "Final details for your assessment" },
];

// Smart defaults (only for: era, type, condition, budget)
const SMART_DEFAULTS = {
  property_era: "classic",
  dwelling_type: "semi-detached",
  condition: "fair",
  budget: "none",
};

const INITIAL_STATE = {
  // Screen 1 - Property Basics
  property_era: SMART_DEFAULTS.property_era,
  dwelling_type: SMART_DEFAULTS.dwelling_type,
  bedrooms: "",
  bathrooms: "",
  epc_band: "",
  // Screen 2 - Layout & Features
  separate_kitchen: "",
  wide_hallways: "no",
  layout_changed: "no",
  features: [],
  // Screen 3 - Assessment Context
  client_type: "",
  condition: SMART_DEFAULTS.condition,
  budget: SMART_DEFAULTS.budget,
  main_goal: "",
  notes: "",
};

export default function PartnerExpress() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  
  const [currentScreen, setCurrentScreen] = useState(1);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
  const [data, setData] = useState(INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [sessionInfo, setSessionInfo] = useState({ first_name: "", postcode: "" });

  // Load session info from sessionStorage
  useEffect(() => {
    const storedSession = sessionStorage.getItem(`partner_session_${sessionId}`);
    if (storedSession) {
      const parsed = JSON.parse(storedSession);
      setSessionInfo({
        first_name: parsed.first_name || "Client",
        postcode: parsed.postcode || "",
      });
    }
  }, [sessionId]);

  const canProceed = () => {
    switch (currentScreen) {
      case 1:
        return isPropertyBasicsComplete(data);
      case 2:
        return isLayoutFeaturesComplete(data);
      case 3:
        return isAssessmentContextComplete(data);
      default:
        return false;
    }
  };

  const goNext = () => {
    if (currentScreen < 3) {
      setDirection(1);
      setCurrentScreen(currentScreen + 1);
    } else {
      handleSubmit();
    }
  };

  const goBack = () => {
    if (currentScreen > 1) {
      setDirection(-1);
      setCurrentScreen(currentScreen - 1);
    } else {
      navigate("/partner");
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const partnerContext = {
        client_type: data.client_type,
        condition: data.condition,
        budget_indication: data.budget,
        notes: data.notes?.trim() || null,
      };
      
      // Filter out "none" from features
      const features = (data.features || []).filter(f => f !== "none");
      
      const payload = {
        first_name: sessionInfo.first_name || "Client",
        postcode: sessionInfo.postcode,
        property_era: data.property_era,
        dwelling_type: data.dwelling_type,
        bedrooms: data.bedrooms,
        bathrooms: parseInt(data.bathrooms) || 1,
        separate_kitchen: data.separate_kitchen,
        wide_hallways_over_1m: data.wide_hallways,
        layout_changed_since_built: data.layout_changed,
        features: features,
        epc_band: data.epc_band,
        main_goal: data.main_goal,
        email: "partner@cipher.app",
        consent: true,
        is_partner: true,
        partner_context: partnerContext,
      };

      const response = await axios.post(`${API}/hippo/teaser`, payload);
      const submissionId = response.headers["x-submission-id"];
      const accessToken = response.headers["x-access-token"];
      
      if (!submissionId) throw new Error("No submission ID returned");
      
      // Store all necessary data for the results page
      sessionStorage.setItem(`hippo_data_${submissionId}`, JSON.stringify(response.data));
      sessionStorage.setItem(`hippo_token_${submissionId}`, accessToken);
      sessionStorage.setItem(`hippo_session_${submissionId}`, JSON.stringify({
        first_name: sessionInfo.first_name,
        postcode: sessionInfo.postcode,
        dwelling_type: data.dwelling_type,
        bedrooms: data.bedrooms,
        features: features,
        separate_kitchen: data.separate_kitchen,
        main_goal: data.main_goal,
      }));
      sessionStorage.setItem(`partner_context_${submissionId}`, JSON.stringify(partnerContext));
      
      navigate(`/hippo/${submissionId}`);
    } catch (error) {
      console.error("Partner submission error:", error);
      toast.error("Failed to generate insight. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
    }),
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 1:
        return <PropertyBasicsScreen data={data} onChange={setData} />;
      case 2:
        return <LayoutFeaturesScreen data={data} onChange={setData} />;
      case 3:
        return <AssessmentContextScreen data={data} onChange={setData} />;
      default:
        return null;
    }
  };

  const screenTitle = SCREEN_TITLES[currentScreen - 1];

  return (
    <div className="fixed inset-0 bg-[#0a0a0a] flex flex-col overflow-hidden">
      {/* Header */}
      <header className="flex-shrink-0 p-4 border-b border-white/10">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={goBack}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/70 hover:text-white"
              aria-label="Go back"
              data-testid="express-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-cipher-green/20 flex items-center justify-center">
                <Briefcase className="w-3.5 h-3.5 text-cipher-green" />
              </div>
              <span className="text-sm font-bold text-white">Partner Express</span>
            </div>
            
            <ThemeToggle />
          </div>
          
          <ExpressProgressBar currentStep={currentScreen} totalSteps={3} />
        </div>
      </header>

      {/* Client Info Bar */}
      <div className="flex-shrink-0 px-4 py-2 bg-white/5 border-b border-white/5">
        <div className="max-w-lg mx-auto flex items-center justify-center gap-3 text-sm">
          <span className="text-white/70">{sessionInfo.first_name}</span>
          <span className="text-white/30">•</span>
          <span className="flex items-center gap-1 text-cipher-green">
            <MapPin className="w-3 h-3" />
            {sessionInfo.postcode}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full max-w-lg mx-auto p-4">
          {/* Screen Title */}
          <div className="mb-6">
            <h1 className="text-xl font-bold text-white">{screenTitle.title}</h1>
            <p className="text-sm text-white/50 mt-1">{screenTitle.subtitle}</p>
          </div>

          {/* Animated Screen Content */}
          <div className="relative overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentScreen}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 },
                }}
                className="overflow-y-auto max-h-[calc(100vh-320px)] pb-4 pr-2"
                style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.2) transparent" }}
              >
                {renderScreen()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>

      {/* Footer with Continue Button */}
      <footer className="flex-shrink-0 p-4 border-t border-white/10 bg-[#0a0a0a]">
        <div className="max-w-lg mx-auto">
          <Button
            onClick={goNext}
            disabled={!canProceed() || submitting}
            className={`w-full py-4 text-base font-medium rounded-xl transition-all ${
              canProceed()
                ? "bg-cipher-green hover:bg-cipher-green/90 text-white shadow-lg shadow-cipher-green/20"
                : "bg-white/10 text-white/40 cursor-not-allowed"
            }`}
            data-testid="express-continue-btn"
          >
            {submitting ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Generating...
              </span>
            ) : currentScreen === 3 ? (
              <span className="flex items-center gap-2">
                Generate Insight
                <ChevronRight className="w-4 h-4" />
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Continue
                <ChevronRight className="w-4 h-4" />
              </span>
            )}
          </Button>
          
          {currentScreen === 3 && (
            <p className="text-[10px] text-white/30 text-center mt-3">
              Partner assessments are auto-unlocked. No client payment required.
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}
